import React, { useState } from "react";
import Navbar from "../../../components/Navbar/Navbar";

import "./Zip.scss";
import Progress from "../../../components/Progress/Progress";
import ScrollToTop from "../../../components/ScrollToTop/ScrollToTop";
import { StepPreFooter } from "../../../components/Footer/StepPreFooter";

import { Controller, useForm } from "react-hook-form";
import { updateContact } from "../../../redux/apiCalls";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";

import {
  Box,
  FormControl,
  FormHelperText,
  TextField,
  InputAdornment,
  CircularProgress
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";

import zipIcon from "../../../assets/images/step-icon/address-icon.png";

import {
  formControlCustom,
  labelCustom,
  textFieldCustom,
  textInputStyles
} from "./style";

import { trimWhiteSpaceObjValue, getPrevStepLink } from "../../../utils/functions";
import { StepBtn } from "../../../components/Button/StepBtn/StepBtn";
import { getLanderPrefill } from "../../../utils/landerPrefill";
import { nextStepDelayTime } from "../../../utils/const";
import { zipLookup } from "../../../utils/lmsApi";

const Zip = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const windowWidth = window.innerWidth;
  const isMobile = windowWidth < 768;

  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingZip, setLoadingZip] = useState(false);

  const contactRedux = useSelector((state) => state?.contact?.contact);
  const tempContact = useSelector((state) => state?.contact?.temporary);

  const [zipCode, setZipCode] = useState(
    tempContact?.zip ?? contactRedux?.zip ?? contactRedux?.zipCode ?? getLanderPrefill()?.zipCode ?? ""
  );
  const [city, setCity] = useState(tempContact?.city ?? contactRedux?.city ?? "");
  const [state, setState] = useState(tempContact?.state ?? contactRedux?.state ?? "");

  const currentLoc = useLocation();
  const prevLink = getPrevStepLink(currentLoc.pathname);

  // CHECK ZIP
  const checkZipCode = async (zip) => {
    if (loadingZip) return null;
    setLoadingZip(true);

    try {
      const result = await zipLookup(zip);
      setLoadingZip(false);

      if (result?.city) {
        setState(result.state);
        setCity(result.city);
        clearErrors("zipCode");
        return { state: result.state, city: result.city };
      } else {
        setState("");
        setCity("");
        setError("zipCode", {
          type: "required",
          message: "Invalid zip code!"
        });
        return null;
      }
    } catch (error) {
      setLoadingZip(false);
      setError("zipCode", {
        type: "required",
        message: "Error checking zip code!"
      });
      return null;
    }
  };

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm();

  // HANDLE SUBMIT FORM
  const handleFormSubmit = async (data) => {
    if (loadingButton) { return; }
    setLoadingButton(true);

    try {
      // Validate zip code
      if (!zipCode || zipCode.length < 5) {
        setError("zipCode", {
          type: "required",
          message: "Zip code must be 5 characters long!"
        });
        setLoadingButton(false);
        return;
      }

      // Check zip code if city/state not already loaded
      let zipData = { state, city };
      if (!state || !city) {
        zipData = await checkZipCode(zipCode);
        if (!zipData) {
          setLoadingButton(false);
          return;
        }
      }

      // DELETE WHITE SPACE FROM OBJECT VALUE
      trimWhiteSpaceObjValue(data);

      const lead_id = sessionStorage.getItem("lead_id");
      
      // Get DOB from temporary contact (stored from home page)
      const dobFromTemp = tempContact?.dob;
      
      const res = await updateContact(dispatch, contactRedux?._id, {
        step: 5,
        lead_id,
        zip: zipCode,
        state: zipData.state,
        city: zipData.city,
        ...(dobFromTemp && { dob: dobFromTemp })
      });

      if (res?.status === 200) {
        setTimeout(() => {
          setLoadingButton(false);
          navigate("/address");
        }, nextStepDelayTime);
      } else {
        setLoadingButton(false);
      }
    } catch (error) {
      console.error("Error updating contact:", error);
      setLoadingButton(false);
    }
  };
  const onSubmit = handleSubmit(handleFormSubmit);

  return (
    <Box className="step-zip">
      <Navbar />
      <ScrollToTop />
      <Box
        sx={{
          width: "100%",
          maxWidth: "700px",
          margin: "20px auto"
        }}
      >
        <div className="step-main">
          <form className="step-form" onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                width: "100%",
                margin: "10px auto 30px",
                padding: isMobile && "0 15px"
              }}
            >
              <Progress step={4} />
            </Box>
            <Box sx={{ textAlign: "center", marginBottom: "11px" }}>
              <img
                style={{
                  maxWidth: isMobile && "42px"
                }}
                src={zipIcon}
                alt=""
              />
            </Box>
            <h2 className="title">What is Your Zip Code?</h2>
            <div className="form-control">
              <FormHelperText sx={labelCustom}>Zip Code</FormHelperText>
              <Controller
                name="zipCode"
                defaultValue={zipCode}
                control={control}
                rules={{
                  required: "Zip code is required!",
                  minLength: {
                    value: 5,
                    message: "Zip code must be 5 characters long!"
                  }
                }}
                render={({ field: { ref, ...field } }) => (
                  <FormControl
                    error={!!errors?.zipCode}
                    size={isMobile ? "small" : "medium"}
                    sx={{ ...formControlCustom, marginBottom: "10px" }}
                    fullWidth
                  >
                    <TextField
                      {...field}
                      inputRef={(input) => {
                        input?.addEventListener("focus", () => {
                          input?.setAttribute("inputmode", "numeric");
                        });
                        ref(input);
                      }}
                      id="zipCode"
                      sx={textFieldCustom}
                      variant="outlined"
                      fullWidth
                      type="text"
                      error={!!errors?.zipCode}
                      label={zipCode ? " " : "e.g. 90011"}
                      helperText={errors?.zipCode && errors?.zipCode?.message}
                      inputProps={{
                        maxLength: 5
                      }}
                      InputLabelProps={{
                        shrink: false,
                        style: { ...textInputStyles, color: "#B7B7B7" }
                      }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        field.onChange(value);
                        setZipCode(value);
                        setState("");
                        setCity("");
                        if (value.length === 5) {
                          checkZipCode(value);
                        }
                      }}
                      onBlur={() => {
                        if (zipCode.length === 5 && !city) {
                          checkZipCode(zipCode);
                        }
                      }}
                      value={zipCode}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        endAdornment: loadingZip && (
                          <InputAdornment position="end">
                            <CircularProgress
                              size={windowWidth > 768 ? 20 : 15}
                              color="inherit"
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                  </FormControl>
                )}
              />
              {city && state && (
                <div
                  className="box-state"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: "15px"
                  }}
                >
                  <LocationOnIcon sx={{ width: 20, color: "#278BA2" }} />
                  <p style={{ margin: 0, color: "#666" }}>
                    {city}, {state}
                  </p>
                </div>
              )}

              <Box
                sx={{
                  maxWidth: "474px",
                  margin: "0 auto"
                }}
              >
                <StepBtn
                  prevLink={prevLink}
                  onSubmit={handleSubmit(onSubmit)}
                  loading={loadingButton}
                />
              </Box>
            </div>
          </form>
          <StepPreFooter />
        </div>
      </Box>
    </Box>
  );
};

export default Zip;
